import React from "react";
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  CheckCircle2,
  Info,
  LucideIcon
} from "lucide-react";

export type AppToastVariant =
  "success" | "error" | "warning" | "info" | "default";

interface VariantStyle {
  icon: LucideIcon;
  /** Badge fill — a light EDS tint, deliberately fixed (not theme-flipping) so
   *  it reads as a bright chip on the dark card and a soft chip on light. */
  badge: string;
}

// Uniform dark-grey icon across all variants — high contrast on the light
// chips, and consistent regardless of theme (EDS neutral-800 light value).
const ICON_COLOUR = "hsl(212 17% 22%)";

const VARIANTS: Record<AppToastVariant, VariantStyle> = {
  success: { icon: CheckCircle2, badge: "hsl(133 64% 91%)" },
  error: { icon: AlertCircle, badge: "hsl(17 100% 91%)" },
  warning: { icon: AlertTriangle, badge: "hsl(42 93% 89%)" },
  info: { icon: Info, badge: "hsl(199 100% 92%)" },
  default: { icon: Bell, badge: "hsl(207 16% 87%)" }
};

interface AppToastProps {
  variant: AppToastVariant;
  message: React.ReactNode;
  /** Optional single action, rendered as a button after the message. */
  action?: { label: string; onClick: () => void };
}

/**
 * The shared toast surface for the app's notifications: a clean themed card
 * with a light coloured icon chip (variant-coloured background + uniform
 * dark-grey icon for strong contrast) and a slightly heavier message — a
 * "corner-of-the-eye" confirmation. Deliberately quiet (no accent strip or
 * countdown bar); the richer, interactive finish-prompt toast is a separate,
 * louder tier.
 */
export const AppToast: React.FC<AppToastProps> = ({
  variant,
  message,
  action
}) => {
  const { icon: Icon, badge } = VARIANTS[variant] ?? VARIANTS.default;
  const assertive = variant === "error" || variant === "warning";

  return (
    <div
      role={assertive ? "alert" : "status"}
      aria-live={assertive ? "assertive" : "polite"}
      aria-atomic="true"
      className="flex items-center gap-2.5 w-[356px] max-w-[calc(100vw-32px)] rounded-xl border border-border bg-popover py-[11px] px-[14px] text-popover-foreground shadow-[0_10px_30px_hsl(var(--shadow-color)/0.28)] text-sm font-medium leading-snug"
      style={{ fontFamily: "var(--font-sans, inherit)" }}
    >
      <span
        aria-hidden="true"
        className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full"
        style={{ background: badge, color: ICON_COLOUR }}
      >
        <Icon size={15} strokeWidth={2.4} />
      </span>
      <span className="flex-1">{message}</span>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="shrink-0 min-h-[24px] rounded-md px-2 text-sm font-semibold text-primary hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};
